import { useState, useEffect } from "react";

const BACKEND_URL = "";

/**
 * TestRunBanner — Shannon's dogfood sentinel.
 *
 * Watches the property address input in Design Studio. When Shannon
 * types one of the sentinel addresses (Fossil Creek Unit 932 + STT
 * fallbacks), a cyan banner appears with a single "Run Full Cascade
 * Now" button. Tapping it fires /api/test-run/cascade which:
 *   1. Seeds a blank NSPIRE walkthrough scaffold
 *   2. Runs the variation + procurement triage
 *   3. Prepares the Resolute Closeout Packet PDF endpoint
 *   4. Snapshots the FWA Launch Pilot pricing math
 *
 * Every record it spawns carries `is_test_run: True` so it never
 * co-mingles with real client data.
 */

const CYAN = "#00FFFF";
const NAVY = "#030B1A";

const TestRunBanner = ({ address }) => {
  const [sentinel, setSentinel] = useState(null);
  const [pilot, setPilot] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  // Watch address changes — probe backend for sentinel match (debounced)
  useEffect(() => {
    setResult(null); setErr("");
    if (!address || address.trim().length < 8) { setSentinel(null); return; }
    const handle = setTimeout(async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/test-run/status?address=${encodeURIComponent(address)}`);
        if (!r.ok) return;
        const d = await r.json();
        setSentinel(d.matched ? d.sentinel : null);
        setPilot(d.matched ? d.pilot_program : null);
      } catch { /* silent */ }
    }, 350);
    return () => clearTimeout(handle);
  }, [address]);

  if (!sentinel) return null;

  const runCascade = async () => {
    if (running) return;
    setRunning(true); setErr(""); setResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/test-run/cascade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          property_address: address,
          market_rent: 1450,
          procurement_cost: 3800,
          mobilization_cost: 1200,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Cascade failed");
      setResult(d);
    } catch (e) {
      setErr(String(e.message || e));
    }
    setRunning(false);
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <div data-testid="test-run-banner" style={{
      background: "linear-gradient(135deg, rgba(0,255,255,0.10) 0%, rgba(0,255,255,0.04) 100%)",
      border: `1px solid ${CYAN}55`,
      borderRadius: 12,
      padding: "14px 16px",
      marginBottom: 14,
      boxShadow: "0 0 22px rgba(0,255,255,0.08)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ color: CYAN, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.22em", fontWeight: 800 }}>
            🧪 TEST RUN MODE ACTIVE · SENTINEL LOCKED
          </div>
          <div style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, marginTop: 4 }}>
            {sentinel.complex_name} · Unit {sentinel.unit_id}
          </div>
          <div style={{ color: "#B4BEC8", fontSize: "0.78rem", marginTop: 2 }}>
            {sentinel.canonical}
          </div>
          <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 3, fontStyle: "italic" }}>
            {sentinel.purpose} · All records tagged <code>is_test_run:true</code>
          </div>
        </div>
        <button
          data-testid="test-run-fire-btn"
          onClick={runCascade}
          disabled={running}
          style={{
            background: running ? "rgba(0,255,255,0.15)" : CYAN,
            color: running ? CYAN : NAVY,
            border: "none", borderRadius: 8,
            padding: "12px 20px",
            fontFamily: "'Inter', sans-serif",
            fontSize: "0.9rem", fontWeight: 800,
            letterSpacing: "0.02em",
            cursor: running ? "wait" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {running ? "Firing cascade…" : "▶ Run Full Cascade Now"}
        </button>
      </div>

      {err && (
        <div data-testid="test-run-error" style={{
          marginTop: 10, padding: "8px 12px",
          background: "rgba(248,113,113,0.10)",
          border: "1px solid rgba(248,113,113,0.35)",
          borderRadius: 8, color: "#F87171", fontSize: "0.82rem",
        }}>{err}</div>
      )}

      {result && (
        <div data-testid="test-run-result" style={{
          marginTop: 12, padding: "12px 14px",
          background: "rgba(2,10,31,0.55)",
          border: "1px solid rgba(0,255,255,0.25)",
          borderRadius: 10,
        }}>
          <div style={{ color: CYAN, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.18em", marginBottom: 8 }}>
            &gt; CASCADE FIRED · {result.cascade_id?.slice(0, 8)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 10 }}>
            <div>
              <div style={{ color: "#7E92A8", fontSize: "0.68rem", letterSpacing: "0.1em", fontFamily: "'JetBrains Mono', monospace" }}>CLIENT PAYS</div>
              <div style={{ color: "#fff", fontSize: "1.1rem", fontWeight: 800 }}>{fmt(result.pilot_pricing?.client_pays_total)}</div>
              <div style={{ color: "#7E92A8", fontSize: "0.7rem" }}>Procurement + 50% Mob</div>
            </div>
            <div>
              <div style={{ color: "#7E92A8", fontSize: "0.68rem", letterSpacing: "0.1em", fontFamily: "'JetBrains Mono', monospace" }}>VALUE WAIVED</div>
              <div style={{ color: "#00DC78", fontSize: "1.1rem", fontWeight: 800 }}>{fmt(result.pilot_pricing?.value_waived_total)}</div>
              <div style={{ color: "#7E92A8", fontSize: "0.7rem" }}>50% Mob + Stab Fee</div>
            </div>
            <div>
              <div style={{ color: "#7E92A8", fontSize: "0.68rem", letterSpacing: "0.1em", fontFamily: "'JetBrains Mono', monospace" }}>STAB FEE (waived)</div>
              <div style={{ color: "#FFB347", fontSize: "1.1rem", fontWeight: 800 }}>{fmt(result.pilot_pricing?.standard_stabilization_fee)}</div>
              <div style={{ color: "#7E92A8", fontSize: "0.7rem" }}>(Rent × 3) + 20%</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <a
              data-testid="test-run-closeout-link"
              href={result.artifacts?.closeout_pdf}
              target="_blank" rel="noreferrer"
              style={{ color: CYAN, textDecoration: "none", border: `1px solid ${CYAN}55`, borderRadius: 6, padding: "6px 10px", fontSize: "0.78rem", fontWeight: 700 }}
            >
              ⬇ Closeout PDF
            </a>
            <a
              data-testid="test-run-rollup-link"
              href={result.artifacts?.roll_up_json}
              target="_blank" rel="noreferrer"
              style={{ color: "#B4BEC8", textDecoration: "none", border: "1px solid rgba(180,190,200,0.3)", borderRadius: 6, padding: "6px 10px", fontSize: "0.78rem", fontWeight: 700 }}
            >
              Roll-up JSON
            </a>
            <span style={{ color: "#7E92A8", fontSize: "0.72rem", alignSelf: "center" }}>
              NSPIRE scaffold: 37 items ready to flip in Field Mode
            </span>
          </div>
          {result.next_steps && result.next_steps.length > 0 && (
            <ol style={{ color: "#B4BEC8", fontSize: "0.78rem", marginTop: 10, paddingLeft: 20, lineHeight: 1.6 }}>
              {result.next_steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          )}
          {pilot && (
            <div style={{ marginTop: 10, padding: "8px 10px", background: "rgba(255,179,71,0.06)", border: "1px solid rgba(255,179,71,0.20)", borderRadius: 8, fontSize: "0.75rem", color: "#FFD59E" }}>
              <b>{pilot.program_name}</b> · {pilot.event} · Limit: {pilot.limit_per_client} unit per {pilot.audience?.split(" / ")[0] || "client"}. Objective: {pilot.objective}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TestRunBanner;
